/**
 * Square API Service
 * 
 * This service handles all interactions with the Square API for inventory management.
 * 
 * SETUP INSTRUCTIONS:
 * 1. Get your Square Access Token from https://developer.squareup.com/
 * 2. Set environment variable: VITE_SQUARE_ACCESS_TOKEN
 * 3. Set environment variable: VITE_SQUARE_LOCATION_ID
 * 
 * For development, this uses localStorage to simulate Square API.
 */

//const SQUARE_API_BASE = 'https://cors-anywhere.com/https://connect.squareup.com/v2';
const SQUARE_API_BASE = 'v2';
const USE_MOCK = !import.meta.env.VITE_SQUARE_ACCESS_TOKEN; // Use mock if no token

class SquareAPI {
  constructor() {
    this.accessToken = import.meta.env.VITE_SQUARE_ACCESS_TOKEN;
    this.locationId = import.meta.env.VITE_SQUARE_LOCATION_ID;
    
    if (USE_MOCK) {
      console.warn('Using mock Square API - configure VITE_SQUARE_ACCESS_TOKEN for production');
      this.initMockStorage();
    }
  }

  /**
   * Initialize mock storage for development
   */
  initMockStorage() {
    if (!localStorage.getItem('square_items')) {
      localStorage.setItem('square_items', JSON.stringify([]));
    }
  }

  /**
   * Get all catalog items
   */
  async getItems() {
    if (USE_MOCK) {
      return this.getMockItems();
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/catalog/list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          'Square-Version': '2024-01-18'
        }
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      return this.transformSquareItems(data.objects || []);
    } catch (error) {
      console.error('Error fetching items from Square:', error);
      throw error;
    }
  }

  /**
   * Create a new catalog item
   */
  async createItem(item) {
    if (USE_MOCK) {
      return this.createMockItem(item);
    }

    try {
      const catalogObject = this.transformToSquareItem(item);
      
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          'Square-Version': '2024-01-18'
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          object: catalogObject
        })
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      return this.transformSquareItem(data.catalog_object);
    } catch (error) {
      console.error('Error creating item in Square:', error);
      throw error;
    }
  }

  /**
   * Update a catalog item
   */
  async updateItem(itemId, updates) {
    if (USE_MOCK) {
      return this.updateMockItem(itemId, updates);
    }

    try {
      const catalogObject = this.transformToSquareItem(updates, itemId);
      
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          'Square-Version': '2024-01-18'
        },
        body: JSON.stringify({
          idempotency_key: this.generateIdempotencyKey(),
          object: catalogObject
        })
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      const data = await response.json();
      return this.transformSquareItem(data.catalog_object);
    } catch (error) {
      console.error('Error updating item in Square:', error);
      throw error;
    }
  }

  /**
   * Delete a catalog item
   */
  async deleteItem(itemId) {
    if (USE_MOCK) {
      return this.deleteMockItem(itemId);
    }

    try {
      const response = await fetch(`${SQUARE_API_BASE}/catalog/object/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Square-Version': '2024-01-18'
        }
      });

      if (!response.ok) {
        throw new Error(`Square API error: ${response.statusText}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting item from Square:', error);
      throw error;
    }
  }

  /**
   * Transform Square catalog item to our format
   */
  transformSquareItem(squareItem) {
    const itemData = squareItem.item_data || {};
    const variations = (itemData.variations || []).map(v => ({
      id: v.id,
      color: v.item_variation_data?.name?.split(' - ')[0] || '',
      size: v.item_variation_data?.name?.split(' - ')[1] || '',
      sku: v.item_variation_data?.sku || '',
      price: v.item_variation_data?.price_money?.amount / 100 || 0,
      quantity: 0 // Would need to fetch from inventory API
    }));

    return {
      id: squareItem.id,
      name: itemData.name || '',
      sku: itemData.sku || '',
      defaultPrice: variations[0]?.price || 0,
      variations: variations,
      createdAt: squareItem.created_at
    };
  }

  /**
   * Transform Square catalog items array
   */
  transformSquareItems(squareItems) {
    return squareItems
      .filter(item => item.type === 'ITEM')
      .map(item => this.transformSquareItem(item));
  }

  /**
   * Transform our item format to Square catalog object
   */
  transformToSquareItem(item, itemId = null) {
    const variations = (item.variations || []).map(v => ({
      type: 'ITEM_VARIATION',
      id: v.id || `#${v.sku}`,
      item_variation_data: {
        name: `${v.color} - ${v.size}`,
        sku: v.sku,
        pricing_type: 'FIXED_PRICING',
        price_money: {
          amount: Math.round(v.price * 100),
          currency: 'USD'
        }
      }
    }));

    return {
      type: 'ITEM',
      id: itemId || '#temp_id',
      item_data: {
        name: item.name,
        sku: item.sku,
        variations: variations
      }
    };
  }

  /**
   * Generate idempotency key for Square API
   */
  generateIdempotencyKey() {
    return `${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  // ============== MOCK IMPLEMENTATION ==============

  getMockItems() {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    return Promise.resolve(items);
  }

  createMockItem(item) {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    const newItem = {
      ...item,
      id: `mock_${Date.now()}_${Math.random().toString(36).substring(7)}`
    };
    items.push(newItem);
    localStorage.setItem('square_items', JSON.stringify(items));
    return Promise.resolve(newItem);
  }

  updateMockItem(itemId, updates) {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    const index = items.findIndex(item => item.id === itemId);
    
    if (index === -1) {
      return Promise.reject(new Error('Item not found'));
    }

    items[index] = { ...items[index], ...updates };
    localStorage.setItem('square_items', JSON.stringify(items));
    return Promise.resolve(items[index]);
  }

  deleteMockItem(itemId) {
    const items = JSON.parse(localStorage.getItem('square_items') || '[]');
    const filtered = items.filter(item => item.id !== itemId);
    localStorage.setItem('square_items', JSON.stringify(filtered));
    return Promise.resolve({ success: true });
  }
}

export const squareAPI = new SquareAPI();
